import { useAuth } from "@agentech/auth-client";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { config } from "../config";

interface AdminInfo {
  userId: string;
  email: string;
  role: "super_admin" | "support";
}

interface AdminContextValue {
  isAdmin: boolean;
  isLoading: boolean;
  admin: AdminInfo | null;
  error: string | null;
  checkAdminStatus: () => Promise<void>;
}

const AdminContext = createContext<AdminContextValue | null>(null);

interface AdminProviderProps {
  children: ReactNode;
}

export function AdminProvider({ children }: AdminProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [admin, setAdmin] = useState<AdminInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setIsAdmin(false);
      setAdmin(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${config.apiBaseUrl}/v1/admin/check`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to check admin status");
      }

      const data = await response.json();

      setIsAdmin(data.isAdmin);
      setAdmin(data.admin ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setIsAdmin(false);
      setAdmin(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) {
      checkAdminStatus();
    }
  }, [authLoading, checkAdminStatus]);

  return (
    <AdminContext.Provider
      value={{
        isAdmin,
        isLoading: isLoading || authLoading,
        admin,
        error,
        checkAdminStatus,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextValue {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
}
